import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { normalizePageMode, PAGE_MODE_STORAGE_KEY, readPageMode, writePageMode } from '../utils/paperSize';

/**
 * Workspace layout state for the app shell (CONTRACTS-W2 §A): where Inky's dock sits and how
 * big it is, the sidebar rail, focus mode, the editor page width, and the seed-a-prompt bridge
 * pages use to hand text to Inky without sending it.
 *
 * Persisted (localStorage `quill.workspaceLayout.v1`, best-effort): dock side/open/sizes/floating
 * rect, sidebar collapsed, editor page mode. Session-only: focus mode, the overlay (narrow screen)
 * dock, the mobile nav drawer, the command palette and shortcuts dialog, pending prompts.
 *
 * Pages outside React's reach of this context (or that should not depend on it) hand prompts to
 * Inky with `askInky()` from src/components/pagekit/askInky.js; the dock turns that window event
 * into an `openInky()` call.
 */

export const WORKSPACE_LAYOUT_STORAGE_KEY = 'quill.workspaceLayout.v1';

/** Dock sides, in the order Mod+\ cycles through them. */
export const DOCK_SIDES = ['right', 'bottom', 'left', 'floating'];

/** Size limits (px). Side docks are widths, the bottom dock is a height. */
export const DOCK_LIMITS = {
  side: { min: 300, max: 720, defaultSize: 380, maxViewportShare: 0.55 },
  bottom: { min: 180, max: 720, defaultSize: 300, maxViewportShare: 0.7 },
  floating: { minWidth: 320, minHeight: 360, defaultWidth: 400, defaultHeight: 600, margin: 12 },
};

/** Sidebar widths (px). */
export const SIDEBAR_WIDTH = 248;
export const SIDEBAR_RAIL_WIDTH = 64;

const DEFAULT_LAYOUT = {
  dock: {
    side: 'right',
    open: true,
    sizes: { right: DOCK_LIMITS.side.defaultSize, left: DOCK_LIMITS.side.defaultSize, bottom: DOCK_LIMITS.bottom.defaultSize },
    floatingRect: null,
  },
  sidebarCollapsed: false,
  editorPageMode: 'page',
};

/**
 * Clamp a number into [min, max]; non-numbers become `fallback`.
 * @param {unknown} value - Value.
 * @param {number} min - Lower bound.
 * @param {number} max - Upper bound.
 * @param {number} fallback - Used when value is not a finite number.
 * @returns {number} Clamped value.
 */
export function clampNumber(value, min, max, fallback) {
  const n = typeof value === 'number' && Number.isFinite(value) ? value : fallback;
  return Math.round(Math.min(Math.max(n, min), Math.max(min, max)));
}

/**
 * Size limits for a dock side at the current viewport.
 * @param {'right'|'left'|'bottom'} side - Dock side.
 * @param {{width: number, height: number}} [viewport] - Viewport size.
 * @returns {{min: number, max: number, defaultSize: number}} Limits.
 */
export function getDockSizeLimits(side, viewport = currentViewport()) {
  if (side === 'bottom') {
    const { min, max, defaultSize, maxViewportShare } = DOCK_LIMITS.bottom;
    return { min, max: Math.max(min, Math.min(max, Math.floor(viewport.height * maxViewportShare))), defaultSize };
  }
  const { min, max, defaultSize, maxViewportShare } = DOCK_LIMITS.side;
  return { min, max: Math.max(min, Math.min(max, Math.floor(viewport.width * maxViewportShare))), defaultSize };
}

/**
 * Viewport size (falls back to a desktop size outside the browser).
 * @returns {{width: number, height: number}} Size in CSS px.
 */
export function currentViewport() {
  if (typeof window === 'undefined') return { width: 1440, height: 900 };
  return { width: window.innerWidth || 1440, height: window.innerHeight || 900 };
}

/**
 * Default floating window rect: top-right, inside the viewport.
 * @param {{width: number, height: number}} [viewport] - Viewport size.
 * @returns {{x: number, y: number, width: number, height: number}} Rect.
 */
export function defaultFloatingRect(viewport = currentViewport()) {
  const { defaultWidth, defaultHeight, margin } = DOCK_LIMITS.floating;
  const width = Math.min(defaultWidth, viewport.width - margin * 2);
  const height = Math.min(defaultHeight, viewport.height - 96 - margin);
  return clampFloatingRect({ x: viewport.width - width - 24, y: 80, width, height }, viewport);
}

/**
 * Keep a floating rect inside the viewport (shrinking it when the viewport is smaller).
 * @param {{x: number, y: number, width: number, height: number}} rect - Rect.
 * @param {{width: number, height: number}} [viewport] - Viewport size.
 * @returns {{x: number, y: number, width: number, height: number}} Clamped rect.
 */
export function clampFloatingRect(rect, viewport = currentViewport()) {
  const { minWidth, minHeight, margin } = DOCK_LIMITS.floating;
  const maxWidth = Math.max(minWidth, viewport.width - margin * 2);
  const maxHeight = Math.max(minHeight, viewport.height - margin * 2);
  const width = clampNumber(rect?.width, Math.min(minWidth, maxWidth), maxWidth, DOCK_LIMITS.floating.defaultWidth);
  const height = clampNumber(rect?.height, Math.min(minHeight, maxHeight), maxHeight, DOCK_LIMITS.floating.defaultHeight);
  const x = clampNumber(rect?.x, margin, viewport.width - width - margin, viewport.width - width - 24);
  const y = clampNumber(rect?.y, margin, viewport.height - height - margin, 80);
  return { x, y, width, height };
}

/**
 * Validate a persisted layout blob, filling defaults and clamping sizes.
 * @param {unknown} raw - Parsed JSON (or anything).
 * @returns {typeof DEFAULT_LAYOUT} Safe layout.
 */
export function sanitizeLayout(raw) {
  const source = raw && typeof raw === 'object' ? raw : {};
  const dock = source.dock && typeof source.dock === 'object' ? source.dock : {};
  const sizes = dock.sizes && typeof dock.sizes === 'object' ? dock.sizes : {};
  const side = DOCK_SIDES.includes(dock.side) ? dock.side : DEFAULT_LAYOUT.dock.side;
  const sideLimits = DOCK_LIMITS.side;
  const bottomLimits = DOCK_LIMITS.bottom;
  const rect = dock.floatingRect && typeof dock.floatingRect === 'object' ? dock.floatingRect : null;
  return {
    dock: {
      side,
      open: typeof dock.open === 'boolean' ? dock.open : DEFAULT_LAYOUT.dock.open,
      sizes: {
        right: clampNumber(sizes.right, sideLimits.min, sideLimits.max, sideLimits.defaultSize),
        left: clampNumber(sizes.left, sideLimits.min, sideLimits.max, sideLimits.defaultSize),
        bottom: clampNumber(sizes.bottom, bottomLimits.min, bottomLimits.max, bottomLimits.defaultSize),
      },
      floatingRect: rect && ['x', 'y', 'width', 'height'].every((k) => Number.isFinite(rect[k]))
        ? { x: Math.round(rect.x), y: Math.round(rect.y), width: Math.round(rect.width), height: Math.round(rect.height) }
        : null,
    },
    sidebarCollapsed: source.sidebarCollapsed === true,
    editorPageMode: normalizePageMode(source.editorPageMode),
  };
}

/**
 * Read the persisted layout.
 * @returns {typeof DEFAULT_LAYOUT} Layout.
 */
export function readStoredLayout() {
  let layout;
  try {
    const raw = window.localStorage.getItem(WORKSPACE_LAYOUT_STORAGE_KEY);
    layout = sanitizeLayout(raw ? JSON.parse(raw) : null);
  } catch {
    layout = sanitizeLayout(null);
  }
  // The view preference lives under its own key (see PAGE_MODE_STORAGE_KEY); the copy in the
  // layout blob is only a fallback for people who chose a view before the key existed.
  const pageMode = readPageMode();
  return pageMode ? { ...layout, editorPageMode: pageMode } : layout;
}

/**
 * Persist the layout (best-effort).
 * @param {typeof DEFAULT_LAYOUT} layout - Layout.
 * @returns {void}
 */
export function writeStoredLayout(layout) {
  try {
    window.localStorage.setItem(WORKSPACE_LAYOUT_STORAGE_KEY, JSON.stringify(layout));
  } catch {
    /* storage unavailable or full: layout still works for this session */
  }
}

/**
 * How the dock is presented at this viewport.
 * - 'push': an in-flow column (right/left at lg+, bottom at md+) that the page reflows around.
 * - 'floating': a draggable window inside the viewport (md+).
 * - 'overlay': a sheet over the page with a scrim (narrow screens).
 * @param {string} side - Dock side.
 * @param {{isMdUp: boolean, isLgUp: boolean}} screen - Breakpoint flags.
 * @returns {'push'|'floating'|'overlay'} Variant.
 */
export function computeDockVariant(side, { isMdUp, isLgUp }) {
  if (side === 'floating') return isMdUp ? 'floating' : 'overlay';
  if (side === 'bottom') return isMdUp ? 'push' : 'overlay';
  return isLgUp ? 'push' : 'overlay';
}

/**
 * Quote a text selection for Inky's composer ("> line" per line, then a blank line).
 * @param {string} text - Selected text.
 * @param {string} [ask] - Optional question appended after the quote.
 * @returns {string} Prompt text.
 */
export function quoteForInky(text, ask = '') {
  const trimmed = String(text || '').trim();
  if (!trimmed) return ask;
  const quoted = trimmed
    .split(/\r?\n/)
    .map((line) => `> ${line}`)
    .join('\n');
  return `${quoted}\n\n${ask}`;
}

const WorkspaceLayoutContext = createContext(null);

/**
 * Provider for the workspace layout. Mount once inside the router and theme.
 * @param {{children: React.ReactNode}} props - Props.
 * @returns {JSX.Element} Provider.
 */
export function WorkspaceLayoutProvider({ children }) {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'), { noSsr: true });
  const isLgUp = useMediaQuery(theme.breakpoints.up('lg'), { noSsr: true });

  const [layout, setLayout] = useState(readStoredLayout);
  const [focusMode, setFocusModeState] = useState(false);
  const [focusDockOpen, setFocusDockOpen] = useState(false);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [shortcutsHelpOpen, setShortcutsHelpOpen] = useState(false);
  const [pendingPrompt, setPendingPrompt] = useState(null);
  const [composerChips, setComposerChips] = useState(null);
  const [composerFocusToken, setComposerFocusToken] = useState(0);
  const [editorChromeCount, setEditorChromeCount] = useState(0);
  const promptSeqRef = useRef(0);

  const variant = computeDockVariant(layout.dock.side, { isMdUp, isLgUp });
  const variantRef = useRef(variant);
  variantRef.current = variant;
  const focusModeRef = useRef(focusMode);
  focusModeRef.current = focusMode;
  const screenRef = useRef({ isMdUp, isLgUp });
  screenRef.current = { isMdUp, isLgUp };
  const sideRef = useRef(layout.dock.side);
  sideRef.current = layout.dock.side;

  /**
   * Show the dock after it moved to `side`. Moving is an explicit "put Inky there" action, so
   * the session-only open states (focus-mode peek, narrow-screen overlay) open too; otherwise
   * a move into an overlay position (left/right below lg) would make Inky vanish.
   * @param {string} side - New dock side.
   * @returns {void}
   */
  const revealDockAt = useCallback((side) => {
    if (focusModeRef.current) setFocusDockOpen(true);
    else if (computeDockVariant(side, screenRef.current) === 'overlay') setOverlayOpen(true);
  }, []);

  // Persist (debounced). Drags only commit on pointer-up, so this never runs per move.
  useEffect(() => {
    const timer = setTimeout(() => writeStoredLayout(layout), 150);
    return () => clearTimeout(timer);
  }, [layout]);

  // Another tab changed the layout: follow it.
  useEffect(() => {
    const onStorage = (event) => {
      if (!event.newValue) return;
      if (event.key === PAGE_MODE_STORAGE_KEY) {
        const value = normalizePageMode(event.newValue);
        setLayout((prev) => (prev.editorPageMode === value ? prev : { ...prev, editorPageMode: value }));
        return;
      }
      if (event.key !== WORKSPACE_LAYOUT_STORAGE_KEY) return;
      try {
        // Keep this tab's view: the page mode syncs through its own key, and a tab on an older
        // build would otherwise reset 'pages' to 'page' here.
        const next = sanitizeLayout(JSON.parse(event.newValue));
        setLayout((prev) => ({ ...next, editorPageMode: prev.editorPageMode }));
      } catch {
        /* ignore malformed values */
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  /**
   * Patch the dock. `size` applies to the current side; `open` applies to whichever open state is
   * active (focus-mode peek, narrow-screen overlay, or the persisted docked state).
   * @param {{side?: string, size?: number, open?: boolean, floatingRect?: object, sizes?: object}} patch - Changes.
   * @returns {void}
   */
  const setDock = useCallback((patch = {}) => {
    const movesSide = Boolean(patch.side && DOCK_SIDES.includes(patch.side) && patch.side !== sideRef.current);
    const nextVariant = movesSide ? computeDockVariant(patch.side, screenRef.current) : variantRef.current;
    if (typeof patch.open === 'boolean') {
      if (focusModeRef.current) setFocusDockOpen(patch.open);
      else if (nextVariant === 'overlay') setOverlayOpen(patch.open);
    } else if (movesSide) {
      revealDockAt(patch.side);
    }
    if (movesSide) sideRef.current = patch.side;
    setLayout((prev) => {
      const dock = { ...prev.dock, sizes: { ...prev.dock.sizes, ...(patch.sizes || {}) } };
      if (patch.side && DOCK_SIDES.includes(patch.side)) {
        dock.side = patch.side;
        if (patch.side === 'floating' && !dock.floatingRect) dock.floatingRect = defaultFloatingRect();
      }
      if (typeof patch.size === 'number' && dock.side !== 'floating') {
        const limits = getDockSizeLimits(dock.side);
        dock.sizes[dock.side] = clampNumber(patch.size, limits.min, limits.max, limits.defaultSize);
      }
      if (patch.floatingRect) dock.floatingRect = clampFloatingRect(patch.floatingRect);
      if (typeof patch.open === 'boolean' && !focusModeRef.current && nextVariant !== 'overlay') dock.open = patch.open;
      // Moving to a side also opens the persisted dock, so it is there when the overlay ends.
      else if (movesSide && typeof patch.open !== 'boolean') dock.open = true;
      const next = { ...prev, dock };
      return JSON.stringify(next) === JSON.stringify(prev) ? prev : next;
    });
  }, [revealDockAt]);

  const toggleDock = useCallback((force) => {
    const apply = (value) => (typeof force === 'boolean' ? force : !value);
    if (focusModeRef.current) {
      setFocusDockOpen(apply);
      return;
    }
    if (variantRef.current === 'overlay') {
      setOverlayOpen(apply);
      return;
    }
    setLayout((prev) => ({ ...prev, dock: { ...prev.dock, open: apply(prev.dock.open) } }));
  }, []);

  const cycleDockSide = useCallback(() => {
    const index = DOCK_SIDES.indexOf(sideRef.current);
    const side = DOCK_SIDES[(index + 1) % DOCK_SIDES.length];
    sideRef.current = side;
    revealDockAt(side);
    setLayout((prev) => ({
      ...prev,
      dock: {
        ...prev.dock,
        side,
        open: true,
        floatingRect: side === 'floating' && !prev.dock.floatingRect ? defaultFloatingRect() : prev.dock.floatingRect,
      },
    }));
  }, [revealDockAt]);

  const setFocusMode = useCallback((on) => {
    const next = Boolean(on);
    setFocusModeState(next);
    setFocusDockOpen(false);
    // The nav drawer never survives a focus-mode change (it is not rendered in focus mode).
    setMobileNavOpen(false);
  }, []);
  const toggleFocusMode = useCallback(() => setFocusMode(!focusModeRef.current), [setFocusMode]);

  const setEditorPageMode = useCallback((mode) => {
    const value = writePageMode(mode);
    setLayout((prev) => (prev.editorPageMode === value ? prev : { ...prev, editorPageMode: value }));
  }, []);

  const setSidebarCollapsed = useCallback((collapsed) => {
    setLayout((prev) => (prev.sidebarCollapsed === Boolean(collapsed) ? prev : { ...prev, sidebarCollapsed: Boolean(collapsed) }));
  }, []);
  const toggleSidebar = useCallback(() => {
    setLayout((prev) => ({ ...prev, sidebarCollapsed: !prev.sidebarCollapsed }));
  }, []);

  /**
   * Open Inky and optionally seed the composer. By default the prompt is only placed in the
   * composer so the user reviews it and presses Enter; `send: true` (an explicit user action such
   * as a "Run with Inky" button) asks the dock to send it once placed. A running turn is never
   * interrupted: the dock leaves the prompt in the composer instead.
   * @param {{prompt?: string, focus?: boolean, send?: boolean, chips?: Array<{label: string, append?: string}>}} [options] - Seed text; focus the
   *   composer (default true); send it after placing (default false); one-click guidance chips
   *   shown above the composer for this handoff only (each appends `append` to the composer text
   *   and sends; a chip without `append` sends the text as is).
   * @returns {void}
   */
  const openInky = useCallback(({ prompt, focus = true, send = false, chips = null } = {}) => {
    toggleDock(true);
    if (typeof prompt === 'string' && prompt.trim()) {
      promptSeqRef.current += 1;
      setPendingPrompt({ id: promptSeqRef.current, text: prompt, ...(send === true ? { send: true } : {}) });
      // Chips belong to this seeded text: they show only while the composer still starts with it.
      setComposerChips(Array.isArray(chips) && chips.length && send !== true ? { id: promptSeqRef.current, chips, seed: prompt } : null);
    }
    if (focus) setComposerFocusToken((n) => n + 1);
  }, [toggleDock]);

  /** Hide the handoff's guidance chips (after sending, or when the composer is cleared). */
  const clearComposerChips = useCallback(() => setComposerChips(null), []);

  /**
   * Mark a seeded prompt as placed in the composer.
   * @param {number} id - Prompt id from `pendingPrompt`.
   * @returns {void}
   */
  const consumePendingPrompt = useCallback((id) => {
    setPendingPrompt((current) => (current && current.id === id ? null : current));
  }, []);

  /**
   * Editor pages (template and document editors) bring their own header with back navigation,
   * so the shell hides its top bar and in-flow sidebar while one is mounted. The sidebar stays
   * reachable as a drawer (`setMobileNavOpen(true)`). Returns the cleanup for the page's effect.
   * @returns {() => void} Unregister.
   */
  const registerEditorChrome = useCallback(() => {
    setEditorChromeCount((n) => n + 1);
    return () => setEditorChromeCount((n) => Math.max(0, n - 1));
  }, []);
  const editorChrome = editorChromeCount > 0;

  // Leaving narrow screens closes the overlay; a docked layout takes over again.
  useEffect(() => {
    if (variant !== 'overlay') setOverlayOpen(false);
  }, [variant]);

  const dockOpen = focusMode ? focusDockOpen : variant === 'overlay' ? overlayOpen : layout.dock.open;
  const dockSize = layout.dock.side === 'floating' ? null : layout.dock.sizes[layout.dock.side];

  const value = useMemo(() => ({
    dock: {
      side: layout.dock.side,
      size: dockSize,
      sizes: layout.dock.sizes,
      open: dockOpen,
      floatingRect: layout.dock.floatingRect,
      variant,
    },
    setDock,
    toggleDock,
    cycleDockSide,
    openInky,
    pendingPrompt,
    consumePendingPrompt,
    composerChips,
    clearComposerChips,
    composerFocusToken,
    focusMode,
    setFocusMode,
    toggleFocusMode,
    editorPageMode: layout.editorPageMode,
    setEditorPageMode,
    sidebarCollapsed: layout.sidebarCollapsed,
    setSidebarCollapsed,
    toggleSidebar,
    mobileNavOpen,
    setMobileNavOpen,
    commandPaletteOpen,
    setCommandPaletteOpen,
    shortcutsHelpOpen,
    setShortcutsHelpOpen,
    editorChrome,
    registerEditorChrome,
    screen: { isMdUp, isLgUp },
  }), [
    layout,
    dockSize,
    dockOpen,
    variant,
    setDock,
    toggleDock,
    cycleDockSide,
    openInky,
    pendingPrompt,
    consumePendingPrompt,
    composerChips,
    clearComposerChips,
    composerFocusToken,
    focusMode,
    setFocusMode,
    toggleFocusMode,
    setEditorPageMode,
    setSidebarCollapsed,
    toggleSidebar,
    mobileNavOpen,
    commandPaletteOpen,
    shortcutsHelpOpen,
    editorChrome,
    registerEditorChrome,
    isMdUp,
    isLgUp,
  ]);

  return <WorkspaceLayoutContext.Provider value={value}>{children}</WorkspaceLayoutContext.Provider>;
}

/** Inert fallback so components rendered outside the shell (tests, marketing pages) still work. */
const FALLBACK = {
  dock: { side: 'right', size: DOCK_LIMITS.side.defaultSize, sizes: DEFAULT_LAYOUT.dock.sizes, open: false, floatingRect: null, variant: 'overlay' },
  setDock: () => {},
  toggleDock: () => {},
  cycleDockSide: () => {},
  openInky: () => {},
  pendingPrompt: null,
  consumePendingPrompt: () => {},
  composerChips: null,
  clearComposerChips: () => {},
  composerFocusToken: 0,
  focusMode: false,
  setFocusMode: () => {},
  toggleFocusMode: () => {},
  editorPageMode: 'page',
  setEditorPageMode: () => {},
  sidebarCollapsed: false,
  setSidebarCollapsed: () => {},
  toggleSidebar: () => {},
  mobileNavOpen: false,
  setMobileNavOpen: () => {},
  commandPaletteOpen: false,
  setCommandPaletteOpen: () => {},
  shortcutsHelpOpen: false,
  setShortcutsHelpOpen: () => {},
  editorChrome: false,
  registerEditorChrome: () => () => {},
  screen: { isMdUp: true, isLgUp: true },
};

/**
 * Workspace layout API (see the provider). Outside the provider it returns inert no-ops.
 * @returns {object} Layout state and actions.
 */
export function useWorkspaceLayout() {
  return useContext(WorkspaceLayoutContext) || FALLBACK;
}

/**
 * Mark the calling page as an editor page while it is mounted: the shell hides its top bar and
 * in-flow sidebar (the page header provides back navigation and a menu button that opens the
 * sidebar as a drawer). A layout effect, so the shell never paints its top bar first.
 * @returns {void}
 */
export function useEditorChrome() {
  const { registerEditorChrome } = useWorkspaceLayout();
  useLayoutEffect(() => registerEditorChrome(), [registerEditorChrome]);
}

export default WorkspaceLayoutContext;
